// Gemini API utility for nutrition facts
// API key is injected at runtime to support different env var prefixes (EXPO_PUBLIC_ vs VITE_)

/**
 * Call Gemini API to get nutrition facts for ingredients
 * @param {string} apiKey - The Gemini API key (injected by the caller)
 * @param {string} modelName - The Gemini model to use (e.g. gemini-flash-lite-latest)
 * @param {Array} ingredients - Array of [name, { number, unit, raw }] entries
 * @returns {Promise<Object>} - Nutrition facts object for total recipe
 */
export const getNutritionFactsFromGemini = async (apiKey, modelName, ingredients) => {
    if (!apiKey) {
        throw new Error('Gemini API key not provided.');
    }
    
    const model = modelName || 'gemini-flash-lite-latest';
    const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`;

    const ingredientLines = ingredients.map(([name, weightData]) => {
        if (weightData.number === 0 && weightData.raw) {
            return `- ${name}: ${weightData.raw}`;
        }
        return `- ${name}: ${weightData.number}${weightData.unit}`;
    }).join('\n');

    const prompt = `You are a professional nutritionist. Calculate the TOTAL nutrition facts for ALL of the following ingredients combined.

## Rules
1. For each ingredient, look up its nutrition data per 100g from USDA FoodData Central.
2. Scale the nutrition data by the exact weight given.
3. Sum all ingredients together to get the TOTAL for the entire recipe.
4. Units: calories in kcal, fats/carbs/protein/fiber/sugar in grams (g), cholesterol and sodium in milligrams (mg).
5. For non-standard units (个, 根, 勺, 盒, 杯, etc.), estimate a reasonable weight in grams first, then calculate.
   Common conversions: 1个鸡蛋≈60g, 1瓣蒜≈5g, 1勺≈15ml, 1大勺≈15ml, 1杯≈240ml, 1片姜≈5g, 1根葱≈15g, 1根胡萝卜≈150g
6. If an ingredient's weight is unknown or ambiguous, use a small reasonable estimate.
7. Round all values to 1 decimal place, except calories (round to integer) and cholesterol/sodium (round to integer).

## Ingredients
${ingredientLines}

## Response Format
Return ONLY a valid JSON object with no markdown, no explanation, no code fences:
{"calories":0,"totalFat":0,"saturatedFat":0,"transFat":0,"polyunsaturatedFat":0,"monounsaturatedFat":0,"cholesterol":0,"sodium":0,"totalCarbohydrates":0,"dietaryFiber":0,"sugar":0,"addedSugar":0,"sugarAlcohols":0,"netCarbs":0,"protein":0}`;

    try {
        const response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }],
                generationConfig: {
                    temperature: 0,
                    topP: 1,
                    responseMimeType: 'application/json',
                }
            })
        });

        if (!response.ok) {
            throw new Error(`Gemini API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.error) {
            throw new Error(`Gemini API error: ${data.error.message || data.error}`);
        }

        if (!data.candidates || !data.candidates[0]) {
            throw new Error('Invalid response from Gemini API: no candidates');
        }

        if (!data.candidates[0].content) {
            throw new Error('Invalid response from Gemini API: no content');
        }

        if (!data.candidates[0].content.parts || !data.candidates[0].content.parts[0]) {
            throw new Error('Invalid response from Gemini API: no parts');
        }

        const responseText = data.candidates[0].content.parts[0].text;

        if (!responseText) {
            throw new Error('Invalid response from Gemini API: no text content');
        }

        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            throw new Error('No valid JSON found in Gemini response');
        }

        const nutritionFacts = JSON.parse(jsonMatch[0]);

        const requiredFields = [
            'calories', 'totalFat', 'saturatedFat', 'transFat', 'polyunsaturatedFat', 'monounsaturatedFat',
            'cholesterol', 'sodium', 'totalCarbohydrates', 'dietaryFiber', 'sugar', 'addedSugar',
            'sugarAlcohols', 'netCarbs', 'protein'
        ];
        for (const field of requiredFields) {
            if (typeof nutritionFacts[field] !== 'number') {
                throw new Error(`Invalid nutrition facts: missing or invalid ${field}`);
            }
        }

        // Validate reasonable ranges
        if (nutritionFacts.calories < 0 || nutritionFacts.calories > 10000) {
            throw new Error('Calories out of reasonable range (0-10000)');
        }
        if (nutritionFacts.totalFat < 0 || nutritionFacts.totalFat > 500) {
            throw new Error('Total fat out of reasonable range (0-500g)');
        }
        if (nutritionFacts.protein < 0 || nutritionFacts.protein > 500) {
            throw new Error('Protein out of reasonable range (0-500g)');
        }
        if (nutritionFacts.totalCarbohydrates < 0 || nutritionFacts.totalCarbohydrates > 1000) {
            throw new Error('Total carbohydrates out of reasonable range (0-1000g)');
        }
        if (nutritionFacts.sodium < 0 || nutritionFacts.sodium > 10000) {
            throw new Error('Sodium out of reasonable range (0-10000mg)');
        }
        if (nutritionFacts.cholesterol < 0 || nutritionFacts.cholesterol > 2000) {
            throw new Error('Cholesterol out of reasonable range (0-2000mg)');
        }

        return nutritionFacts;
    } catch (error) {
        throw error;
    }
};

/**
 * Fallback nutrition calculation when API fails
 * @param {Array} ingredients - Array of ingredients with weights
 * @returns {Object} - Comprehensive nutrition facts
 */
export const getFallbackNutritionFacts = (ingredients) => {
    let totalWeight = 0;
    ingredients.forEach(([name, weightData]) => {
        totalWeight += weightData.number;
    });

    return {
        calories: Math.round(totalWeight * 1.5),
        totalFat: Math.round(totalWeight * 0.05),
        saturatedFat: Math.round(totalWeight * 0.02),
        transFat: 0,
        polyunsaturatedFat: Math.round(totalWeight * 0.01),
        monounsaturatedFat: Math.round(totalWeight * 0.02),
        cholesterol: Math.round(totalWeight * 0.5),
        sodium: Math.round(totalWeight * 2),
        totalCarbohydrates: Math.round(totalWeight * 0.2),
        dietaryFiber: Math.round(totalWeight * 0.02),
        sugar: Math.round(totalWeight * 0.05),
        addedSugar: 0,
        sugarAlcohols: 0,
        netCarbs: Math.round(totalWeight * 0.18),
        protein: Math.round(totalWeight * 0.1)
    };
};
